import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, ToggleButton, ToggleButtonGroup, CircularProgress, Typography, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { supabase } from '../supabase';
import { accent } from '../utils/colorDict';

const COLLECTION = process.env.REACT_APP_COLLECTION || null;
// Sentinel passed to the RPC to mean "WHERE collection IS NULL"
// (the unified-site deployment). The RPC's p_collection contract:
//   null            -> aggregate every row (cross-site "All")
//   '__unified__'   -> rows with collection IS NULL only (unified site)
//   anything else   -> exact collection match
const UNIFIED_SENTINEL = '__unified__';

interface Histogram {
  lt_10s: number;
  '10s_1m': number;
  '1m_5m': number;
  '5m_30m': number;
  gte_30m: number;
}

interface Stats {
  total_events: number;
  unique_sessions: number;
  authenticated_sessions: number;
  anonymous_sessions: number;
  unique_users: number;
  events_by_type: Record<string, number> | null;
  daily_sessions: Array<{ date: string; sessions: number }> | null;
  session_duration_histogram: Histogram | null;
  session_duration_histogram_anonymous: Histogram | null;
  top_referrers: Array<{ host: string; sessions: number }> | null;
  top_searches: Array<{ query: string; count: number }> | null;
  top_geotaggers: Array<{ user_id: string; username: string; count: number }> | null;
}

const StatCard = ({ label, value, color = accent }: any) => (
  <div style={{ flex: 1, minWidth: 100, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6, textAlign: 'center' }}>
    <Typography variant="h4" sx={{ color, fontWeight: 600 }}>{value?.toLocaleString() ?? '—'}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </div>
);

// Session-duration histogram — a single horizontal stacked bar so it stays
// compact at the bottom of the dashboard. Bucket order is left to right
// (short visits → long).
const HISTOGRAM_BUCKETS: Array<{ key: keyof Histogram; label: string; color: string }> = [
  { key: 'lt_10s',  label: '<10s',    color: '#bdbdbd' },
  { key: '10s_1m',  label: '10s–1m',  color: '#90caf9' },
  { key: '1m_5m',   label: '1–5m',    color: '#4fc3f7' },
  { key: '5m_30m',  label: '5–30m',   color: '#0288d1' },
  { key: 'gte_30m', label: '≥30m',    color: '#01579b' },
];
const SessionHistogram = ({ data }: { data: Histogram | null }) => {
  if (!data) return <Typography variant="caption" color="text.secondary">No data</Typography>;
  const total = HISTOGRAM_BUCKETS.reduce((s, b) => s + (data[b.key] || 0), 0);
  if (total === 0) return <Typography variant="caption" color="text.secondary">No sessions in this period</Typography>;
  return (
    <div>
      <div style={{ display: 'flex', height: 22, width: '100%', borderRadius: 4, overflow: 'hidden' }}>
        {HISTOGRAM_BUCKETS.map(b => {
          const count = data[b.key] || 0;
          if (count === 0) return null;
          const pct = (count / total) * 100;
          return (
            <div key={b.key} style={{ width: `${pct}%`, backgroundColor: b.color }} title={`${b.label}: ${count} (${pct.toFixed(1)}%)`} />
          );
        })}
      </div>
      <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
        {HISTOGRAM_BUCKETS.map(b => (
          <Typography key={b.key} variant="caption" sx={{ color: '#555' }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, backgroundColor: b.color, borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />
            {b.label}: <strong>{(data[b.key] || 0).toLocaleString()}</strong>
          </Typography>
        ))}
      </Stack>
    </div>
  );
};

// Tiny inline SVG bar chart for daily sessions
const DailyChart = ({ data }: { data: Array<{ date: string; sessions: number }> }) => {
  if (!data || !data.length) return <Typography variant="caption" color="text.secondary">No data</Typography>;
  const max = Math.max(...data.map(d => d.sessions), 1);
  const width = 600;
  const height = 80;
  const barWidth = Math.max(1, width / data.length - 2);
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height + 20}`} style={{ overflow: 'visible' }}>
      {data.map((d, i) => {
        const h = (d.sessions / max) * height;
        return (
          <g key={d.date}>
            <rect x={i * (barWidth + 2)} y={height - h} width={barWidth} height={h} fill={accent} />
            <title>{d.date}: {d.sessions}</title>
          </g>
        );
      })}
      {/* Date labels: first, middle, last */}
      <text x={0} y={height + 14} fontSize="10" fill="#666">{data[0]?.date}</text>
      {data.length > 2 && <text x={width / 2} y={height + 14} fontSize="10" fill="#666" textAnchor="middle">{data[Math.floor(data.length / 2)]?.date}</text>}
      <text x={width} y={height + 14} fontSize="10" fill="#666" textAnchor="end">{data[data.length - 1]?.date}</text>
    </svg>
  );
};

const AnalyticsDashboard = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [days, setDays] = useState(30);
  // Default scope: when running on a per-collection deployment we pre-select
  // its toggle; the unified-site deployment defaults to its own scope so the
  // admin sees that site's activity (not the cross-site rollup) on open.
  const [scope, setScope] = useState<string>(COLLECTION || UNIFIED_SENTINEL);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    // 'all' -> null (no collection filter). The UNIFIED_SENTINEL passes through
    // as-is; the SQL function recognises it and applies `collection IS NULL`.
    const collParam = scope === 'all' ? null : scope;
    supabase.rpc('get_engagement_stats', { p_collection: collParam, p_days: days })
      .then(({ data, error }) => {
        if (error) setError(error.message);
        else setStats(data as Stats);
        setLoading(false);
      });
  }, [open, days, scope]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Engagement Analytics
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <ToggleButtonGroup size="small" value={scope} exclusive onChange={(_, v) => v && setScope(v)}>
            <ToggleButton value="all">All sites</ToggleButton>
            <ToggleButton value={UNIFIED_SENTINEL}>Unified</ToggleButton>
            <ToggleButton value="scholten">Scholten</ToggleButton>
            <ToggleButton value="aleksandrowicz">Aleksandrowicz</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" value={days} exclusive onChange={(_, v) => v && setDays(v)}>
            <ToggleButton value={7}>7d</ToggleButton>
            <ToggleButton value={30}>30d</ToggleButton>
            <ToggleButton value={90}>90d</ToggleButton>
            <ToggleButton value={365}>1y</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {loading && <div style={{ textAlign: 'center', padding: 40 }}><CircularProgress /></div>}
        {error && <Typography color="error">{error}</Typography>}

        {!loading && !error && stats && (
          <Stack spacing={2}>
            {/* Top stats */}
            <Stack direction="row" spacing={1}>
              <StatCard label="Total events" value={stats.total_events} />
              <StatCard label="Unique sessions" value={stats.unique_sessions} color="#4caf50" />
              <StatCard label="Anonymous sessions" value={stats.anonymous_sessions} color="#9e9e9e" />
              <StatCard label="Logged-in sessions" value={stats.authenticated_sessions} color="#ff9800" />
              <StatCard label="Logged-in users" value={stats.unique_users} color="#e64a19" />
            </Stack>

            {/* Daily activity */}
            <div>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Daily sessions</Typography>
              <DailyChart data={stats.daily_sessions || []} />
            </div>

            {/* Session-duration histogram — all visitors and anonymous-only */}
            <div>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Session duration (all visitors)</Typography>
              <SessionHistogram data={stats.session_duration_histogram} />
            </div>
            {stats.anonymous_sessions > 0 && (
              <div>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Session duration (anonymous only)</Typography>
                <SessionHistogram data={stats.session_duration_histogram_anonymous} />
              </div>
            )}

            {/* Top referrers — hostname-bucketed, with '(direct)' for empty referrers */}
            {stats.top_referrers && stats.top_referrers.length > 0 && (
              <div>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Top referrers</Typography>
                <Stack spacing={0.5}>
                  {stats.top_referrers.map(r => (
                    <div key={r.host} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 8px', backgroundColor: '#fafafa', fontSize: 13 }}>
                      <span style={{ color: r.host === '(direct)' ? '#888' : undefined, fontStyle: r.host === '(direct)' ? 'italic' : undefined }}>{r.host}</span>
                      <strong>{r.sessions.toLocaleString()}</strong>
                    </div>
                  ))}
                </Stack>
              </div>
            )}

            {/* Events by type */}
            {stats.events_by_type && (
              <div>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Events by type</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {Object.entries(stats.events_by_type).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                    <div key={type} style={{ padding: '4px 10px', backgroundColor: '#e0f2f7', borderRadius: 4, fontSize: 13 }}>
                      <strong>{count.toLocaleString()}</strong> {type}
                    </div>
                  ))}
                </Stack>
              </div>
            )}

            {/* Top geotaggers */}
            {stats.top_geotaggers && stats.top_geotaggers.length > 0 && (
              <div>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Top geotaggers</Typography>
                <Stack spacing={0.5}>
                  {stats.top_geotaggers.map(g => (
                    <div key={g.user_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 8px', backgroundColor: '#fafafa', fontSize: 13 }}>
                      <span>{g.username || '(unknown)'}</span>
                      <strong>{g.count}</strong>
                    </div>
                  ))}
                </Stack>
              </div>
            )}

            {/* Top searches */}
            {stats.top_searches && stats.top_searches.length > 0 && (
              <div>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Top searches</Typography>
                <Stack spacing={0.5}>
                  {stats.top_searches.slice(0, 15).map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 8px', backgroundColor: '#fafafa', fontSize: 13 }}>
                      <span>"{s.query}"</span>
                      <strong>{s.count}</strong>
                    </div>
                  ))}
                </Stack>
              </div>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AnalyticsDashboard;
